/**
 * Sign up — email-first passwordless via WebAuthn passkey registration.
 *
 * 🔴 STEP ONE OF TWO (V057). This page no longer creates anything: it asks the server to
 * EMAIL a link, and the account is created only when the client clicks it
 * (CompleteSignup.tsx). Signup used to take an email straight from this form and bind
 * whoever answered the Face ID prompt to whatever account that email named — so anyone
 * who knew a client's address could take over their account.
 * Returning users go to `/login` instead.
 *
 *   1. user types email → "Continue with passkey"
 *   2. POST /register/begin → PublicKeyCredentialCreationOptions
 *   3. browser prompts for biometric / security key (creation)
 *   4. POST /register/finish → server verifies attestation + persists
 *   5. POST /login/begin → PublicKeyCredentialRequestOptions for the just-registered cred
 *   6. browser re-prompts for assertion (some authenticators skip when fresh)
 *   7. POST /login/finish → server mints session JWT
 *   8. saveSession() + navigate("/")
 */

import { isPasskeySupported } from "@re-platform/auth";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";

import { APP_NAME, SUPPORT_EMAIL } from "../../brand";
import { inviteTokenFor, loadOnboardingAgent } from "../home/onboardingAgent";
import { AuthBrand, type AuthNavState } from "./AuthBrand";
import { computeDeviceSignal } from "./deviceSignal";
import {
  passkeyApi,
  TenantAmbiguousError,
  type TenantChoice,
} from "./passkeyApi";
import "./SignIn.css";
import { PublicFooter } from "../../shell/PublicFooter";

type Status =
  | { kind: "idle" }
  | { kind: "loading"; stage: "register" | "prompt" | "verifying" | "login" }
  | { kind: "error"; message: string };

export function SignIn() {
  const [email, setEmail] = useState("");
  const [displayName] = useState("");
  const [supported, setSupported] = useState<boolean | null>(null);
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  //: The link has been emailed. Deliberately the same screen whatever the address was.
  const [sent, setSent] = useState(false);
  // Set when the email is a client in >1 brokerage (Finding 3): the user picks
  // one and we retry the whole flow scoped to that tenant_id.
  const [ambiguous, setAmbiguous] = useState<TenantChoice[] | null>(null);
  // Pre-populated from /join or /b/:slug landing pages. Router state is
  // ephemeral (lost on reload / a second navigation hop), so fall back to the
  // invite context JoinPage persists in sessionStorage — without it a signup
  // silently self-mints a personal tenant instead of joining the brokerage
  // (caught live at Deploy Checkpoint #9).
  const location = useLocation();
  const navState = location.state as AuthNavState | null;
  const presetTenantId =
    navState?.tenantId ?? loadOnboardingAgent()?.agentTenantId;
  // Only known when navigated here from /b/:slug or /b/:slug/a/:agentId — a
  // direct visit to /sign-up has no tenant to brand for, so it falls back to
  // the generic mark rather than a mismatched lookup. A dual-licensed agent's
  // page sends BOTH brokerage marks (see AuthBrand).
  const brandName = navState?.brandName;
  const brands = navState?.brands;

  useEffect(() => {
    setSupported(isPasskeySupported());
  }, []);

  async function run(tenantId?: string): Promise<void> {
    setAmbiguous(null);
    setStatus({ kind: "loading", stage: "register" });
    try {
      // 🔴 Step ONE of TWO. We do NOT create an account here, and we do not touch the
      // passkey yet: the server emails a link, and the account is created only when the
      // client clicks it. That ordering is the account-takeover fix — signup used to
      // take an email straight from this form and bind whoever's Face ID answered the
      // prompt to whatever account that email named.
      //
      // The response says the same thing whether the address is new, already registered,
      // or one we refuse. Do not branch on it; the branching happens in their inbox.
      await passkeyApi.requestSignup({
        email: email.trim(),
        display_name: displayName.trim() || undefined,
        tenant_id: tenantId,
        // Spec 023 — only toward the brokerage the invite came from (or when no
        // brokerage is preset, which leaves the server's own guard to decide).
        invite_token: presetTenantId
          ? inviteTokenFor(presetTenantId)
          : loadOnboardingAgent()?.inviteToken,
        device_signal: await computeDeviceSignal(),
      });
      setSent(true);
      setStatus({ kind: "idle" });
    } catch (err) {
      if (err instanceof TenantAmbiguousError) {
        setAmbiguous(err.candidates);
        setStatus({ kind: "idle" });
        return;
      }
      setStatus({
        kind: "error",
        message: err instanceof Error ? err.message : "Something went wrong",
      });
    }
  }

  async function handleSubmit(
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();
    await run(presetTenantId);
  }

  if (supported === false) {
    return (
      <main className="signin">
        <section className="signin__card">
          <h1>Passkeys aren't supported here</h1>
          <p>
            Your browser doesn't support WebAuthn passkeys yet. Try the latest
            Chrome, Safari, Firefox, or Edge on a device with Touch ID, Face ID,
            Windows Hello, or a security key.
          </p>
        </section>
        <PublicFooter />
      </main>
    );
  }

  if (sent) {
    return (
      <main className="signin">
        <section className="signin__card">
          <AuthBrand
            brandName={brandName}
            brands={brands}
            fallback={<KeyRound size={28} aria-hidden />}
          />
          <h1>Check your inbox</h1>
          {/* Deliberately identical whatever the address was — new, already registered,
              or one we refuse. Telling the visitor which would let anyone probe the
              brokerage for who is house-hunting and who is selling. */}
          <p>
            If that address can have an account, we've emailed it a link. Open
            it to finish setting up — the link works once and expires in 30
            minutes.
          </p>
          <p className="signin__hint">
            Already have an account? The email we sent will take you straight to
            signing in.
          </p>
        </section>
        <PublicFooter />
      </main>
    );
  }

  return (
    <main className="signin">
      <section className="signin__card signin__card--split">
        <aside className="signin__aside">
          <div className="signin__aside-brand">
            <span className="signin__aside-mark">
              <ShieldCheck size={18} aria-hidden />
            </span>
            {APP_NAME}
          </div>
          <div>
            <div className="signin__aside-title">Set up in a few taps</div>
            <p className="signin__aside-sub">
              We email you a link. Open it, then save a passkey with Face ID or
              your fingerprint. There is no password to remember.
            </p>
          </div>
          <div className="signin__aside-note">
            <ShieldCheck size={15} aria-hidden /> Passkeys, not passwords
          </div>
        </aside>

        <div className="signin__main">
          <div className="signin__eyebrow">New here</div>
          <h1>Create your account</h1>
          <p className="signin__subtitle">
            Enter your email and we'll send you a link to finish setting up.
          </p>

          <form className="signin__form" onSubmit={handleSubmit}>
            <label className="signin__field">
              <span>Email address</span>
              <input
                type="email"
                required
                autoComplete="username webauthn"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={status.kind === "loading"}
              />
            </label>

            <button
              type="submit"
              className="signin__cta rp-btn rp-btn--primary"
              disabled={status.kind === "loading" || !email.trim()}
            >
              {status.kind === "loading" ? (
                <>
                  <Loader2 className="signin__spin" size={18} aria-hidden />
                  {status.stage === "register" && "Sending your link…"}
                  {status.stage === "prompt" && "Confirm with your device…"}
                  {status.stage === "verifying" && "Verifying…"}
                  {status.stage === "login" && "Signing you in…"}
                </>
              ) : (
                <>
                  <ShieldCheck size={18} aria-hidden />
                  Email me a sign-up link
                </>
              )}
            </button>
          </form>

          {ambiguous && (
            <div
              className="signin__choices"
              role="group"
              aria-label="Choose your brokerage"
            >
              <p className="signin__subtitle">
                This email is registered with more than one brokerage. Which
                one?
              </p>
              {ambiguous.map((choice) => (
                <button
                  key={choice.tenant_id}
                  type="button"
                  className="signin__choice rp-btn rp-btn--secondary"
                  onClick={() => void run(choice.tenant_id)}
                >
                  {choice.tenant_name}
                </button>
              ))}
            </div>
          )}

          {status.kind === "error" && (
            <div className="signin__error" role="alert">
              <strong>Something went wrong.</strong>
              <p>{status.message}</p>
            </div>
          )}

          <p className="signin__footnote">
            Already have an account? <Link to="/login">Sign in</Link>.
          </p>
          <p className="signin__footnote">
            <a href={`mailto:${SUPPORT_EMAIL}`}>Contact us</a>
          </p>
        </div>
      </section>
      <PublicFooter />
    </main>
  );
}
